/**
 * API COVERAGE — PlatformAuthController (/api/platform/auth) [SaaS-owner plane]
 *   POST /login · POST /refresh-token · POST /logout · GET /profile → platformAuthApi.*
 */
import { api } from '@/lib/apiClient';
import type { AuthResult } from '@/types/auth';
import type { PagedResult } from '@/types/api';
import type { AiOpsMetrics } from '@/types/aiOps';
import type { GetTenantsParams, PlatformDashboard, PlatformTenant, PlatformTenantProfile } from '@/types/platform';

// SaaS-owner (platform) authentication plane: /api/platform/auth/*.
// Separate from the tenant AuthController. Returns the same token shape (no tenant fields
// on the profile). Mapped for completeness; the tenant app does not use it by default.
export interface PlatformProfile {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  userType: string;
  roles: string[];
  permissions: string[];
}

export const platformAuthApi = {
  login: (body: { email: string; password: string }) =>
    api.post<AuthResult>('/platform/auth/login', body),
  refreshToken: (refreshToken: string) =>
    api.post<AuthResult>('/platform/auth/refresh-token', { refreshToken }),
  logout: (refreshToken: string) => api.post<unknown>('/platform/auth/logout', { refreshToken }),
  profile: () => api.get<PlatformProfile>('/platform/auth/profile'),
};

/**
 * API COVERAGE — PlatformTenantsController (/api/platform/tenants) [PlatformAuthorize]
 *   GET  /api/platform/tenants            → platformTenantsApi.list (search, status, paging)
 *   GET  /api/platform/tenants/{id}       → platformTenantsApi.get
 *   POST /api/platform/tenants/{id}/suspend  → platformTenantsApi.suspend
 *   POST /api/platform/tenants/{id}/activate → platformTenantsApi.activate
 *   GET  /api/platform/tenants/{id}/profile  → platformTenantsApi.profile
 *   POST /api/platform/tenants/{id}/trial/extend → platformTenantsApi.extendTrial
 */
/**
 * A custom domain mapped to a tenant (Sections 3.1, 3.2).
 *
 * `isVerified` is the gate that matters: the resolver only routes traffic for a verified domain, so
 * a claim nobody has checked cannot capture requests.
 */
export interface TenantDomain {
  id: number;
  tenantId: number;
  domain: string;
  isPrimary: boolean;
  isVerified: boolean;
  createdAt: string;
  /** Website, Erp, Vendor, B2B or Customer. */
  portal?: string;
  /** False while that portal is not built: the address shows "coming soon". */
  portalBuilt?: boolean;
}

export const platformTenantDomainsApi = {
  list: (tenantId: number) => api.get<TenantDomain[]>(`/platform/tenants/${tenantId}/domains`),
  /** The company's own domain: its five addresses with fixed sub-domains. Empty takes it off. */
  setBase: (tenantId: number, baseDomain: string | null) =>
    api.put<TenantDomain[]>(`/platform/tenants/${tenantId}/domains/base`, { baseDomain }),
  add: (tenantId: number, domain: string) =>
    api.post<TenantDomain>(`/platform/tenants/${tenantId}/domains`, { domain }),
  setVerified: (tenantId: number, domainId: number, isVerified: boolean) =>
    api.put<TenantDomain>(`/platform/tenants/${tenantId}/domains/${domainId}/verified`, { isVerified }),
  setPrimary: (tenantId: number, domainId: number) =>
    api.put<TenantDomain>(`/platform/tenants/${tenantId}/domains/${domainId}/primary`, {}),
  remove: (tenantId: number, domainId: number) =>
    api.del<unknown>(`/platform/tenants/${tenantId}/domains/${domainId}`),
};

export const platformTenantsApi = {
  list: (params: GetTenantsParams) =>
    api.get<PagedResult<PlatformTenant>>('/platform/tenants', params as Record<string, unknown>),
  get: (id: number) => api.get<PlatformTenant>(`/platform/tenants/${id}`),
  suspend: (id: number) => api.post<PlatformTenant>(`/platform/tenants/${id}/suspend`),
  activate: (id: number) => api.post<PlatformTenant>(`/platform/tenants/${id}/activate`),
  /** O13 tenant page: business profile from onboarding, trial and suggested plan. */
  profile: (id: number) => api.get<PlatformTenantProfile>(`/platform/tenants/${id}/profile`),
  /** Super admin only. Returns the refreshed tenant page. */
  extendTrial: (id: number, days: number, reason: string) =>
    api.post<PlatformTenantProfile>(`/platform/tenants/${id}/trial/extend`, { days, reason }),
};

/**
 * API COVERAGE — PlatformDashboardController (/api/platform/dashboard) [PlatformAuthorize]
 *   GET /api/platform/dashboard → platformDashboardApi.get (master-derived KPIs, charts, MRR/ARR, recent activity)
 */
export const platformDashboardApi = {
  get: () => api.get<PlatformDashboard>('/platform/dashboard'),
};

/**
 * API COVERAGE — AiOpsController (/api/platform/ai-ops) [PlatformAuthorize]
 *   GET /api/platform/ai-ops/metrics → platformAiOpsApi.metrics (cross-tenant monitoring snapshot)
 */
export const platformAiOpsApi = {
  metrics: () => api.get<AiOpsMetrics>('/platform/ai-ops/metrics'),
};
